import React, { useEffect, useRef, useState } from 'react';
import MobileControls, { lockPlayViewport } from './NetplayRoom.mobile';
import './NetplayRoom.css';
import './GbaLinkedRoom.css';

// Injected transport: getSnapshot()/subscribe(fn) use private {id,lease,room};
// request({action,generation,...}, {signal}?) and upload(File,{generation,signal})
// resolve {ok,code?,id,lease,room,data?}. Join/session storage/socket lifecycle are
// Main's responsibility. Optional synchronous nextInputSequence() allocates a
// nonnegative safe integer for the live identity/lease generation across UI mounts.
// attachDisplay(canvas,{seat,generation,memberId}) returns
// cleanup and consumes native RGBA frames; never load a core/ROM or spawn an emulator.
// onActivateAudio is supplied by the independent native PCM/audio lifecycle.
const EMPTY = { id: null, lease: null, room: { generation: 0, revision: 0, phase: 'empty', members: [], capabilities: {} } };
const KEYS = { ArrowUp: 4, ArrowDown: 5, ArrowLeft: 6, ArrowRight: 7, KeyZ: 0, KeyX: 8, ShiftLeft: 2, ShiftRight: 2, Enter: 3, KeyQ: 10, KeyW: 11 };
const metadataAction = action => ['named_list','named_delete'].includes(action);
// MobileControls/keyboard use libretro IDs. Wire/native input is GBA10 held bits.
export function toGbaMask(mask) {
  return [[8,0],[0,1],[2,2],[3,3],[7,4],[6,5],[4,6],[5,7],[11,8],[10,9]]
    .reduce((value,[source,target]) => value | ((mask & (1 << source)) ? 1 << target : 0),0);
}
const MESSAGES = { HOST_ONLY: 'Only the connected host can do that.', STALE_GENERATION: 'The room changed. Try again.',
  INVALID_LEASE: 'Controller lease expired. Reconnecting controls…', TIMEOUT: 'The native operation timed out. Refresh the room.',
  UNAVAILABLE: 'Native service is unavailable.', RUNTIME_REJECTED: 'The native service did not acknowledge this operation.',
  UNSUPPORTED: 'This runtime does not support named saves.', RATE_LIMIT: 'Too many requests. Wait and try again.' };

export default function GbaLinkedRoom({ transport, attachDisplay, onActivateAudio,
  keyboardActive = true, onSelectControls, sidebarExtras }) {
  const [snapshot,setSnapshot] = useState(EMPTY), [error,setError] = useState(''),
    [controllerError,setControllerError] = useState(''),
    [busy,setBusy] = useState(false), [mask,setMask] = useState(0), [saveName,setSaveName] = useState(''),
    [saves,setSaves] = useState([]), [expanded,setExpanded] = useState(false), [viewNotice,setViewNotice] = useState(''),
    [overrideOpen,setOverrideOpen] = useState(false), [overrideCode,setOverrideCode] = useState('');
  const current = useRef(EMPTY), alive = useRef(false), activeTransport = useRef(), canvas = useRef(),
    panel = useRef(), fullscreenOwned = useRef(false), operation = useRef(), sequence = useRef(0), keys = useRef(new Map()), touches = useRef(new Map()), padMask = useRef(0),
    input = useRef({ sending: false, dirty: false, mask: 0, pending: [] }), actions = useRef({}), pendingPause = useRef(null);
  const connected = s => Boolean(s.id && s.room.members.some(m => m.id === s.id && m.connected !== false));
  const canControl = () => { const s = current.current; return (!operation.current || metadataAction(operation.current.action)) && connected(s) && s.room.phase === 'running' &&
    s.lease?.generation === s.room.generation && s.lease.expiresAt > Date.now(); };
  const cancelOperation = () => { pendingPause.current = null; operation.current?.controller.abort(); operation.current = null; if (alive.current) setBusy(false); };
  const nextInputSequence = t => {
    const seq = typeof t.nextInputSequence === 'function' ? t.nextInputSequence() : sequence.current++;
    if (!Number.isSafeInteger(seq) || seq < 0) throw new Error('INPUT_SEQUENCE');
    return seq;
  };
  const accept = next => {
    if (!next?.room || !Array.isArray(next.room.members)) return;
    const previous = current.current;
    if (next.room.generation < previous.room.generation || (next.room.generation === previous.room.generation && next.room.revision < previous.room.revision)) return;
    const changed = next.id !== previous.id || next.room.generation !== previous.room.generation || next.lease?.token !== previous.lease?.token;
    const op = operation.current;
    if (op && (next.id !== op.id || (next.room.hostId !== op.hostId && op.action !== 'override') || next.room.generation > op.generation)) {
      // Own native action advances one generation; any other transition cancels UI work.
      if (next.id === op.id && next.room.generation === op.generation + 1 && next.room.pendingAction === op.action && next.room.phase === 'transitioning') op.generation++;
      else cancelOperation();
    }
    const intent = pendingPause.current;
    if (intent && (operation.current !== intent || next.id !== intent.id || next.room.hostId !== intent.hostId ||
      next.room.generation !== intent.generation || !connected(next) || next.room.game?.id !== previous.room.game?.id ||
      !['running','transitioning'].includes(next.room.phase))) pendingPause.current = null;
    if (changed || next.room.phase !== 'running') {
      input.current.controller?.abort(); input.current={sending:false,dirty:false,mask:0,pending:[]}; keys.current.clear(); touches.current.clear(); padMask.current=0;
      setMask(0); if (changed) sequence.current = 0;
    }
    // Do not roll back a renewed private lease on an older, same-revision reply.
    if (!changed && next.lease && previous.lease && next.lease.expiresAt < previous.lease.expiresAt)
      next = { ...next, lease: previous.lease };
    current.current = next; setSnapshot(next);
    if (next.room.game?.id !== previous.room.game?.id || next.room.hostId !== previous.room.hostId) setSaves([]);
  };
  const pumpInput = async () => {
    const queue = input.current, t = activeTransport.current;
    if (queue.sending || (!queue.dirty && !queue.pending.length) || !canControl() || !t) return;
    queue.sending = true; queue.dirty = false;
    const s = current.current, lease = s.lease, value = toGbaMask(queue.pending.length ? queue.pending.shift() : queue.mask);
    const controller = new AbortController(); queue.controller=controller;
    let timer;
    const cancelled = new Promise(resolve => {
      controller.signal.addEventListener('abort',()=>resolve({ok:false,code:'CANCELLED'}),{once:true});
      timer=setTimeout(()=>{
        if(alive.current&&t===activeTransport.current&&queue===input.current&&current.current.id===s.id&&current.current.room.generation===s.room.generation&&current.current.lease?.token===lease.token){
          keys.current.clear();touches.current.clear();padMask.current=0;queue.mask=0;queue.pending.length=0;queue.dirty=value!==0;
          setMask(0);setControllerError(previous=>previous||'Controller input timed out; buttons released.');
        }
        controller.abort();
      },3000);
    });
    try {
      const seq = nextInputSequence(t);
      const ack = await Promise.race([cancelled,t.request({ action: 'input', generation: s.room.generation, leaseToken: lease.token, seat: lease.seat, seq, mask: value },{signal:controller.signal})]);
      if (!alive.current || t !== activeTransport.current || queue !== input.current || controller.signal.aborted) return;
      if (ack?.room) accept(ack);
      // A lease/media/operation receipt is not evidence of recovered native input.
      if (controller.signal.aborted) return;
      if (ack?.ok === true) setControllerError('');
      else if (ack?.ok === false && !['CANCELLED','STALE_OPERATION','STALE_GENERATION','NOT_RUNNING'].includes(ack.code)) setControllerError(MESSAGES[ack.code] || 'Controller input was rejected.');
    } catch { if (alive.current && t === activeTransport.current && queue === input.current && !controller.signal.aborted) setControllerError('Controller transport is unavailable.'); }
    finally { clearTimeout(timer); queue.sending = false; if (alive.current && t === activeTransport.current && queue===input.current && (queue.dirty || queue.pending.length)) pumpInput(); }
  };
  const sendHeld = () => {
    let held = padMask.current; keys.current.forEach(bit => { held |= 1 << bit; }); touches.current.forEach(value => { held |= value; });
    const queue=input.current,changed=held!==queue.mask;queue.mask=held;
    // Preserve short press AND release edges behind the real native ACK. One
    // in-flight request, at most8 transitions; repeats/heartbeats add no backlog.
    if(changed){
      if(queue.pending.length>=8){
        keys.current.clear();touches.current.clear();padMask.current=0;queue.mask=0;queue.pending=[0];
        if(alive.current)setControllerError('Controller input queue overflowed; buttons released.');
      }else queue.pending.push(held);
    }
    queue.dirty=true;if(alive.current)setMask(queue.mask);pumpInput();
  };
  // Blur/hide discards unplayed presses; never replay them after losing focus.
  const release = () => { keys.current.clear(); touches.current.clear(); padMask.current=0; input.current.pending.length=0; sendHeld(); };
  const run = async (action, payload = {}, file) => {
    // Auto-pause is more important than an in-flight metadata reply. Its real
    // server generation fence cancels stale metadata too; no local fake pause.
    if(action==='pause'&&metadataAction(operation.current?.action))cancelOperation();
    if (operation.current || !connected(current.current)) return { ok: false, code: 'UNAVAILABLE' };
    pendingPause.current = null;
    const t = activeTransport.current, s = current.current;
    const op = { action, generation: s.room.generation, id: s.id, hostId: s.room.hostId, controller: new AbortController() };
    operation.current = op; setBusy(true); setError('');
    let timer;
    const cancelled = new Promise(resolve => {
      op.controller.signal.addEventListener('abort', () => resolve({ ok: false, code: 'CANCELLED' }), { once: true });
      timer = setTimeout(() => { op.timedOut = true; op.controller.abort(); },['upload','refresh','override','topology','named_save','named_load'].includes(action) ? 120000 : 10000);
    });
    try {
      const ack = await Promise.race([cancelled, Promise.resolve().then(() => {
        if(op.controller.signal.aborted)return{ok:false,code:'CANCELLED'};
        return file ? t.upload(file,{generation: s.room.generation,signal: op.controller.signal}) :
          t.request({action,...payload,generation: s.room.generation},{signal: op.controller.signal});
      })]);
      if (!alive.current || t !== activeTransport.current || operation.current !== op || op.controller.signal.aborted) {
        if (alive.current && operation.current === op && op.timedOut) setError(MESSAGES.TIMEOUT);
        return {ok:false,code:op.timedOut ? 'TIMEOUT' : 'CANCELLED'};
      }
      if (ack?.room) {
        if(ack.room.generation<current.current.room.generation || ack.room.generation>op.generation+1)return{ok:false,code:'CANCELLED'};
        // Ack-only transports may omit the intermediate publication; still fence
        // subsequent responses before consuming save data or override success.
        op.generation=ack.room.generation;accept(ack);
      }
      if(operation.current!==op)return{ok:false,code:'CANCELLED'};
      op.acknowledged = ack?.ok === true;
      // Transport may deliver only the final envelope (without transitioning snapshot).
      if (ack?.ok !== true) setError(MESSAGES[ack?.code] || 'Room operation failed.');
      else if (action.startsWith('named_')) setSaves(Array.isArray(ack.data?.saves) ? ack.data.saves : []);
      return ack || {ok:false,code:'UNAVAILABLE'};
    } catch {
      if (alive.current && operation.current === op) setError('Room transport is unavailable.');
      return {ok:false,code:'UNAVAILABLE'};
    } finally {
      clearTimeout(timer);
      if (operation.current === op) {
        // One intent belongs to this exact capture/action, never a retry queue.
        // Wait for its real ACK; the follow-up pause also needs its own native ACK.
        const intent = pendingPause.current, next = current.current;
        pendingPause.current = null; operation.current = null;
        if (alive.current) {
          setBusy(false);
          if (intent === op && op.acknowledged && t === activeTransport.current && connected(next) &&
            next.id === op.id && next.id === next.room.hostId && next.room.hostId === op.hostId &&
            next.room.generation === op.generation && next.room.phase === 'running' &&
            (document.visibilityState !== 'visible' || !document.hasFocus())) actions.current.run('pause');
        }
      }
    }
  };
  actions.current = { accept, release, canControl, sendHeld, pumpInput, run };
  useEffect(() => {
    alive.current = true; activeTransport.current = transport; current.current = EMPTY;
    input.current = { sending:false,dirty:false,mask:0,pending:[] }; sequence.current = 0; setSnapshot(EMPTY); setBusy(false); setError(''); setControllerError(''); setSaves([]);setOverrideCode('');setOverrideOpen(false);
    const update = next => { if (alive.current && activeTransport.current === transport) actions.current.accept(next); };
    let unsubscribe;
    try { unsubscribe = transport?.subscribe(update); update(transport?.getSnapshot()); } catch { setError('Room transport is unavailable.'); }
    const heartbeat = setInterval(async () => {
      const s = current.current;
      if (!connected(s) || !transport || s.room.phase === 'transitioning') return;
      try {
        const ack = await transport.request({action:'lease',generation:s.room.generation});
        if (alive.current && activeTransport.current === transport && ack?.ok) {
          update(ack); if (actions.current.canControl()) { input.current.dirty=true; actions.current.pumpInput(); }
        }
      } catch { /* Native input deadline prevents a stuck held button during outages. */ }
    },4000);
    return () => {
      // Send neutral independently of a stuck/in-flight input ack before detaching.
      const s=current.current;
      if(actions.current.canControl()){
        try{Promise.resolve(transport.request({action:'input',generation:s.room.generation,leaseToken:s.lease.token,seat:s.lease.seat,seq:nextInputSequence(transport),mask:0})).catch(()=>{});}catch{}
      }
      input.current.controller?.abort(); alive.current=false;keys.current.clear();touches.current.clear();padMask.current=0;
      cancelOperation(); clearInterval(heartbeat); unsubscribe?.(); activeTransport.current=null;
    };
  },[transport]);
  const {room,id,lease} = snapshot, me = room.members.find(m => m.id === id), seat = me?.seat || null;
  useEffect(() => {
    if (!attachDisplay || !canvas.current || !id) return;
    return attachDisplay(canvas.current,{seat:seat || 1,generation:room.generation,memberId:id,...(!seat ? {readOnly:true} : {})});
  },[attachDisplay,seat,id,room.generation]);
  useEffect(() => { if(expanded)return lockPlayViewport(); },[expanded]);
  const exitView = () => {
    actions.current.release();setExpanded(false);setViewNotice('');
    if((document.fullscreenElement || document.webkitFullscreenElement)===panel.current){
      try{Promise.resolve((document.exitFullscreen || document.webkitExitFullscreen)?.call(document)).catch(()=>{});}catch{}
    }
  };
  const maximize = () => {onSelectControls?.();onActivateAudio?.();setExpanded(true);setViewNotice('');};
  const fullscreen = async () => {
    maximize();const request=panel.current?.requestFullscreen || panel.current?.webkitRequestFullscreen;
    if(!request){setViewNotice('Browser fullscreen is unavailable. The game is maximized on this page.');return;}
    try{await request.call(panel.current);}catch{if(alive.current)setViewNotice('Browser fullscreen was blocked. The game is maximized on this page.');}
  };
  useEffect(() => {
    const changed=()=>{
      if((document.fullscreenElement || document.webkitFullscreenElement)===panel.current)fullscreenOwned.current=true;
      else if(fullscreenOwned.current){fullscreenOwned.current=false;actions.current.release();setExpanded(false);}
    };
    const escape=event=>{if(event.key==='Escape')exitView();};
    document.addEventListener('fullscreenchange',changed);document.addEventListener('webkitfullscreenchange',changed);window.addEventListener('keydown',escape);
    return()=>{document.removeEventListener('fullscreenchange',changed);document.removeEventListener('webkitfullscreenchange',changed);window.removeEventListener('keydown',escape);};
  },[]);
  useEffect(() => {
    const key = down => event => {
      const bit=KEYS[event.code]; if (bit === undefined) return;
      if (!down) { if (keys.current.delete(event.code)) actions.current.sendHeld(); return; }
      if (!keyboardActive || !actions.current.canControl() || /^(INPUT|TEXTAREA|SELECT)$/.test(event.target?.tagName) || event.target?.isContentEditable || event.target?.closest?.('[contenteditable="true"]')) return;
      if (event.code === 'Enter' && event.target?.closest?.('button,a')) return;
      event.preventDefault(); keys.current.set(event.code,bit); actions.current.sendHeld();
    };
    let pauseTimer;
    const pauseHost=()=>{
      const s=current.current, op=operation.current;
      if(!alive.current || !connected(s) || s.id!==s.room.hostId)return;
      if(op && !metadataAction(op.action)){
        if(!['pause','resume'].includes(op.action) && (s.room.phase==='running' ||
          s.room.phase==='transitioning' && s.room.pendingAction===op.action)) pendingPause.current=op;
        return;
      }
      if(s.room.phase==='running')actions.current.run('pause');
    };
    const down=key(true),up=key(false),blur=()=>{actions.current.release();clearTimeout(pauseTimer);pauseTimer=setTimeout(()=>{if(!document.hasFocus())pauseHost();},100);};
    const focus=()=>{if(document.visibilityState==='visible'&&document.hasFocus()){clearTimeout(pauseTimer);pendingPause.current=null;}};
    const visibility=()=>{if(document.visibilityState!=='visible'){actions.current.release();pauseHost();}else focus();};
    const pagehide=()=>{actions.current.release();pauseHost();};
    let raf;
    const poll=()=>{
      let held=0;
      if(keyboardActive&&actions.current.canControl()&&document.visibilityState==='visible'&&document.hasFocus()){
        const pad=Array.from(navigator.getGamepads?.()||[]).find(p=>p?.connected);
        if(pad){
          for(const[i,bit]of[[0,0],[1,8],[4,10],[5,11],[8,2],[9,3],[12,4],[13,5],[14,6],[15,7]])if(pad.buttons[i]?.pressed)held|=1<<bit;
          if(pad.axes[0]<-.35)held|=1<<6;if(pad.axes[0]>.35)held|=1<<7;
          if(pad.axes[1]<-.35)held|=1<<4;if(pad.axes[1]>.35)held|=1<<5;
        }
      }
      if(held!==padMask.current){padMask.current=held;actions.current.sendHeld();}raf=requestAnimationFrame(poll);
    };
    raf=requestAnimationFrame(poll);
    window.addEventListener('keydown',down);window.addEventListener('keyup',up);window.addEventListener('blur',blur);window.addEventListener('focus',focus);window.addEventListener('pagehide',pagehide);document.addEventListener('visibilitychange',visibility);
    return()=>{clearTimeout(pauseTimer);pendingPause.current=null;cancelAnimationFrame(raf);input.current.pending.length=0;keys.current.clear();padMask.current=0;actions.current.sendHeld();window.removeEventListener('keydown',down);window.removeEventListener('keyup',up);window.removeEventListener('blur',blur);window.removeEventListener('focus',focus);window.removeEventListener('pagehide',pagehide);document.removeEventListener('visibilitychange',visibility);};
  },[keyboardActive]);
  const online = connected(snapshot), isHost = online && id === room.hostId, disabled = !online || busy || room.phase === 'transitioning';
  const controlDisabled = !online || busy&&!metadataAction(operation.current?.action) || !seat || room.phase !== 'running' || !lease || lease.expiresAt <= Date.now();
  return <section ref={panel} className={'gba-linked-room'+(expanded ? ' gba-linked-room--expanded' : '')} aria-label="Four Swords linked GBA room" onPointerDown={() => onSelectControls?.()}>
    <aside className="gba-linked-rail" aria-label="GBA room controls">
      <h2>Four Swords · GBA link</h2>
      <p role="status">{room.phase} · {gameLabel(room.game)}{seat ? ` · Your controller: P${seat}` : ' · Spectating'}</p>
      {((room.phase === 'transitioning' && ['upload','refresh','override','topology'].includes(room.pendingAction)) ||
        (busy && ['upload','refresh','override','topology'].includes(operation.current?.action))) &&
        <p role="status">Starting linked consoles automatically (about a minute). Controls and saves are disabled until native startup is acknowledged.</p>}
      {isHost && <>
        <button type="button" disabled={disabled || !room.game || !['running','paused'].includes(room.phase)} onClick={() => run(room.phase === 'paused' ? 'resume' : 'pause')}>{room.phase === 'paused' ? 'Resume game' : 'Pause game'}</button>
        <button type="button" disabled={disabled || !room.game} onClick={() => run('refresh')}>Refresh room</button>
        <label className="gba-upload">Upload GBA ROM<input aria-label="Upload GBA ROM" type="file" accept=".gba,.zip" disabled={disabled || !transport?.upload} onChange={event => {const file=event.target.files?.[0];event.target.value='';if(file)run('upload',{},file);}}/></label>
        {room.capabilities.playerCounts?.length>1 && <label>Linked consoles<select aria-label="Linked consoles" disabled={disabled} value={room.nativePlayers||4} onChange={event=>{
          const players=Number(event.target.value);if(room.game&&!window.confirm(`Restart with ${players} linked consoles? Unsaved progress will be lost and controllers above P${players} will be unassigned.`))return;
          run('topology',{players,confirmRestart:Boolean(room.game)});
        }}>{room.capabilities.playerCounts.map(n=><option key={n} value={n}>{n} consoles</option>)}</select></label>}
      </>}
      <button type="button" disabled={!onActivateAudio} onClick={() => onActivateAudio?.()}>Audio</button>
      <button type="button" onClick={maximize}>Maximize page</button>
      <button type="button" onClick={fullscreen}>Full screen</button>
      {expanded && <button type="button" onClick={exitView}>Exit</button>}
      {[1,2,3,4].map(player => <label key={player}>Player {player}<select aria-label={`Player ${player}`} value={room.members.find(m => m.seat === player)?.id || ''} disabled={!isHost || disabled || player>(room.controllerLimit||4)} onChange={event => run('seat',{seat:player,memberId:event.target.value || null})}>
        <option value="">Unassigned</option>{room.members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
      </select></label>)}
      {room.capabilities.hostOverride && <div className="gba-linked-override">
        <button type="button" disabled={disabled} aria-expanded={overrideOpen} onClick={()=>{setOverrideCode('');setOverrideOpen(!overrideOpen);}}>Host Override</button>
        {overrideOpen && <form aria-label="Host override" onSubmit={async event=>{
          event.preventDefault();if(disabled || !/^[0-9]{4}$/.test(overrideCode))return;
          const code=overrideCode;setOverrideCode('');const ack=await run('override',{code});if(alive.current&&ack?.ok)setOverrideOpen(false);
        }}><p>Take host and restart all linked consoles. Unsaved progress will be lost. Guests may use the recovery code too.</p>
          <input aria-label="Host override code" type="password" inputMode="numeric" autoComplete="off" maxLength={4} value={overrideCode} disabled={disabled} onChange={event=>setOverrideCode(event.target.value.replace(/[^0-9]/g,'').slice(0,4))}/>
          <button type="submit" disabled={disabled || !/^[0-9]{4}$/.test(overrideCode)}>Take host &amp; refresh</button>
          <button type="button" disabled={busy} onClick={()=>{setOverrideCode('');setOverrideOpen(false);}}>Cancel</button>
        </form>}
      </div>}
      <h3>Connected players</h3>
      <ul aria-label="Connected roster">{room.members.map(m => <li key={m.id}>{m.name}{m.id === room.hostId ? ' · Host' : ''}{m.seat ? ` · P${m.seat}` : ' · Spectator'}</li>)}</ul>
      {isHost && room.capabilities.namedSaves === true && <div aria-label="Named saves" className="gba-linked-saves">
        <h3>Named saves</h3>
        <p>Captures all four consoles together. A paused save can advance briefly during capture.</p>
        <input aria-label="Save name" value={saveName} maxLength={80} disabled={disabled} onChange={event => setSaveName(event.target.value)}/>
        <button type="button" disabled={disabled || !saveName.trim() || !room.game} onClick={() => run('named_save',{name:saveName})}>Save named state</button>
        <button type="button" disabled={disabled || !room.game} onClick={() => run('named_list')}>List saves</button>
        {saves.map(save => <div key={save.id}><span>{save.name}</span><button type="button" disabled={disabled} onClick={() => run('named_load',{id:save.id})}>Load {save.name}</button><button type="button" disabled={disabled} onClick={() => {if(window.confirm(`Delete named save "${save.name}"?`))run('named_delete',{id:save.id});}}>Delete {save.name}</button></div>)}
      </div>}
      {sidebarExtras}
    </aside>
    <div className="gba-linked-personal">
      <canvas ref={canvas} width={240} height={160} aria-label={seat ? `Player ${seat} personal GBA screen` : 'Player 1 GBA screen (read-only spectator)'}/>
      {!seat && <p>Ask the host to assign you a controller.</p>}
      <MobileControls system="gba" seat={seat || 1} disabled={controlDisabled} touches={touches} sendHeld={sendHeld} canControl={canControl} mask={mask}/>
      <p className="gba-linked-key-hint">Arrows · Z: B · X: A · Shift: Select · Enter: Start · Q/W: L/R</p>
      {(error || controllerError) && <p role="alert">{error || controllerError}</p>}
      {viewNotice && <p role="status">{viewNotice}</p>}
    </div>
  </section>;
}
function gameLabel(game) { return game?.name || 'Host uploads a GBA ROM'; }
